import React, { useState, useRef, useEffect } from "react";
import { Outlet, useNavigate, useLocation, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutDashboard, CalendarDays, Plus, ListChecks, Users, Wallet, Menu, LogOut, Bell, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "الرئيسية", end: true },
  { to: "/admin/calendar", icon: CalendarDays, label: "التقويم" },
  { to: "/admin/new", icon: Plus, label: "حجز جديد" },
  { to: "/admin/bookings", icon: ListChecks, label: "الحجوزات" },
  { to: "/admin/customers", icon: Users, label: "الزبائن" },
  { to: "/admin/accounts", icon: Wallet, label: "الحسابات" },
  { to: "/admin/more", icon: Menu, label: "المزيد" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ req: 0, transfers: 0 });
  const [open, setOpen] = useState(false);
  const prevRef = useRef(null);
  const bellRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [open]);

  const { data: dash } = useQuery({
    queryKey: ["notify-dash"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 5000,
  });

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; g.gain.value = 0.08; o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, 260);
    } catch (e) {}
  };

  useEffect(() => {
    if (!dash) return;
    const req = dash.new_requests?.length || 0;
    const transfers = dash.transfers?.length || 0;
    setCounts({ req, transfers });
    if (prevRef.current) {
      const msgs = [];
      if (req > prevRef.current.req) msgs.push("طلب حجز جديد");
      if (transfers > prevRef.current.transfers) msgs.push("وصل تحويل جديد بانتظار التأكيد");
      if (msgs.length) { playBeep(); msgs.forEach((m) => toast.info(m)); }
    }
    prevRef.current = { req, transfers };
  }, [dash]);

  const total = counts.req + counts.transfers;
  const goto = (path) => { setOpen(false); navigate(path); };
  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen gradient-navy text-offwhite">
      {/* Top bar */}
      <header className="glass sticky top-0 z-30 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-display text-xl gold-text">قمرية</span>
          <span className="text-xs text-muted2 hidden sm:inline">| لوحة الإدارة</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative" ref={bellRef}>
            <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell" className="relative w-9 h-9 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="الإشعارات">
              <Bell size={18} />
              {total > 0 && <span data-testid="notif-badge" className="absolute -top-1 -end-1 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{total}</span>}
            </button>
            {open && (
              <div className="absolute top-11 end-0 w-72 glass rounded-2xl p-2 z-40 space-y-1" data-testid="notif-panel">
                <button onClick={() => goto("/admin/bookings")} data-testid="notif-new-requests" className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-navy3 transition-colors text-sm">
                  <span className="flex items-center gap-2 text-offwhite"><Bell size={15} className="text-gold" /> طلبات حجز جديدة</span>
                  <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-full text-xs ${counts.req > 0 ? "bg-gold text-navy font-bold" : "bg-navy3 text-muted2"}`}>{counts.req}</span>
                </button>
                <button onClick={() => goto("/admin/bookings")} data-testid="notif-transfers" className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-navy3 transition-colors text-sm">
                  <span className="flex items-center gap-2 text-offwhite"><Receipt size={15} className="text-gold" /> تحويلات تنتظر التأكيد</span>
                  <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-full text-xs ${counts.transfers > 0 ? "bg-gold text-navy font-bold" : "bg-navy3 text-muted2"}`}>{counts.transfers}</span>
                </button>
              </div>
            )}
          </div>
          <span className="text-sm text-muted2" data-testid="current-user">{user?.name}</span>
          <button onClick={doLogout} data-testid="logout-btn" className="w-9 h-9 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="خروج">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-56 shrink-0 border-l border-gold/10 min-h-[calc(100vh-56px)] p-4 gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-gold text-navy font-semibold" : "text-muted2 hover:bg-navy3/50 hover:text-offwhite"}`}>
              <n.icon size={19} /> {n.label}
            </NavLink>
          ))}
        </aside>

        <main className="flex-1 p-4 sm:p-6 pb-28 md:pb-6 max-w-5xl mx-auto w-full">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 glass z-30 flex items-center justify-around px-1 py-2">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bnav-${n.label}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px] transition-colors ${isActive ? "text-gold" : "text-muted2"}`}>
            <n.icon size={20} /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
